import { useCallback } from "react";
import { Action, ActionCampaign } from "../../common/types/actions";
import { BookOnboardingCallCard } from "./bookOnboardingCall/BookOnboardingCallCard";
import { ConfirmTransactionsCard } from "./confirmTransactions/ConfirmTransactionsCard";
import { InviteAccountantCard } from "./inviteAccountant/InviteAccountantCard";
import { IRSRequirementsCard } from "./irsRequirements/IRSRequirementsCard";
import { PersonalToBusinessCard } from "./personalToBusiness/PersonalToBusinessCard";
import { SetupReceiptMatchingCard } from "./setupReceiptMatching/SetupReceiptMatchingCard";
import { WelcomeVideoCard } from "./welcomeVideo/WelcomeVideoCard";
import { UploadReceiptsCard } from "./uploadReceipts/UploadReceiptsCard";
import { ReconnectAccountsCard } from "./reconnectAccounts/ReconnectAccountsCard";
import { CategorizeTransactionsCard } from "./categorizeTransactions/CategorizeTransactionsCard";
import { SubstantiateTransactionsCard } from "./substantiateTransactions/SubstantiateTransactionsCard";
import { TransferMatchTransactionsCard } from "./transferMatchTransactions/TransferMatchTransactionsCard";

const CAMPAIGN_CARDS: Record<
    ActionCampaign,
    React.ComponentType<CampaignCardProps<any>>
> = {
    [ActionCampaign.BOOK_ONBOARDING_CALL]: BookOnboardingCallCard,
    [ActionCampaign.CONFIRM_TRANSACTIONS]: ConfirmTransactionsCard,
    [ActionCampaign.INVITE_ACCOUNTANT]: InviteAccountantCard,
    [ActionCampaign.IRS_REQUIREMENTS]: IRSRequirementsCard,
    [ActionCampaign.UPLOAD_RECEIPTS]: UploadReceiptsCard,
    [ActionCampaign.PERSONAL_TO_BUSINESS]: PersonalToBusinessCard,
    [ActionCampaign.SETUP_RECEIPT_MATCHING]: SetupReceiptMatchingCard,
    [ActionCampaign.WELCOME_VIDEO]: WelcomeVideoCard,
    [ActionCampaign.RECONNECT_ACCOUNTS]: ReconnectAccountsCard,
    [ActionCampaign.CATEGORIZE_TRANSACTIONS]: CategorizeTransactionsCard,
    [ActionCampaign.SUBSTANTIATE_TRANSACTIONS]: SubstantiateTransactionsCard,
    [ActionCampaign.MATCH_TRANSACTIONS]: TransferMatchTransactionsCard,
};

export interface CampaignCardProps<TActionMeta extends object = object> {
    onClick?: () => void;
    isCompleted?: boolean;
    isSkipped?: boolean;
    isLoading?: boolean;
    opened: boolean;
    onCardClosing?: () => void;
    onCardClosed?: () => void;

    action: Action<TActionMeta>;
    onConfirm?: (action: Action, data?: object) => void;
    onSkip?: (action: Action, data?: object) => void;
}

export type ActionItemProps = Omit<
    CampaignCardProps,
    "onClick" | "onCardClosing" | "onCardClosed"
> & {
    onClick: (action: Action) => void;
    onCardClosing: (action: Action) => void;
    onCardClosed: (action: Action) => void;
};

export const ActionItem: React.FC<ActionItemProps> = ({
    onClick,
    onCardClosing,
    onCardClosed,
    ...props
}) => {
    const Card = CAMPAIGN_CARDS[props.action.campaign];

    const handleClick = useCallback(() => {
        onClick(props.action);
    }, [onClick, props.action]);

    const handleCardClosing = useCallback(() => {
        onCardClosing(props.action);
    }, [onCardClosing, props.action]);

    const handleCardClosed = useCallback(() => {
        onCardClosed(props.action);
    }, [onCardClosed, props.action]);

    return (
        <Card
            onClick={handleClick}
            onCardClosing={handleCardClosing}
            onCardClosed={handleCardClosed}
            {...props}
        />
    );
};
